import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useAuth } from '../../contexts/AuthContext';
import { SectionCard } from '../../pages/MyAccountPage';
import SubscriptionsTable from '../notifications/SubscriptionsTable';
import type { Role, User } from '../../types/auth';
import {
  useAdminUserSubscriptions,
  useCapabilities,
  useDeleteUser,
  useResetUserPassword,
  useSendTestSms,
  useSetUserActive,
  useUpdateAdminUserSubscriptions,
  useUpdateUser,
  useUsers,
} from '../../api/admin';
import { requestFieldErrors, requestMessage } from '../../utils/requestError';

/** The organisation a linked colleague works here from. */
export const linkedOrganisationName = (user: User): string | null =>
  user.linked_from_organisation ?? user.home_user?.tenant_name ?? null;

/** How a person signs in, in the words the table and drawer both use. */
export const signInLabel = (user: User): string => {
  if (user.auth_source === 'linked') return 'Through their own organisation';
  if (user.auth_source === 'sso' || user.sso_linked) return 'Single sign-on';
  return 'Password';
};

/* ------------------------------------------------------------------------ */
/* Confirm dialog, shared with the People table                              */
/* ------------------------------------------------------------------------ */

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  /** Red for destructive actions. */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{body}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          disabled={busy}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------------------------------------------ */
/* Drawer                                                                    */
/* ------------------------------------------------------------------------ */

export interface PersonDrawerProps {
  /** The person being viewed; null closes the drawer. */
  userId: number | null;
  roles: Role[];
  onClose: () => void;
}

const DRAWER_WIDTH = 480;

/**
 * Everything an administrator can do to one person's account, in a side
 * drawer over the People table: contact details, sign-in / password, roles
 * and access, notification subscriptions, and deactivate / delete.
 *
 * The body is keyed on the user id so switching people resets every field
 * and mounts the per-user hooks with a fixed id.
 */
export default function PersonDrawer({ userId, roles, onClose }: PersonDrawerProps) {
  const open = userId !== null && Number.isFinite(userId);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          // Resets the Vite template's global `#root { text-align: center }`.
          textAlign: 'left',
        },
      }}
    >
      {open && (
        <PersonDrawerBody key={userId} userId={userId as number} roles={roles} onClose={onClose} />
      )}
    </Drawer>
  );
}

interface SnackState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

function PersonDrawerBody({
  userId,
  roles,
  onClose,
}: {
  userId: number;
  roles: Role[];
  onClose: () => void;
}) {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const { data: users } = useUsers();
  const targetUser = useMemo(
    () => (users ?? []).find((u) => u.id === userId) ?? null,
    [users, userId]
  );
  const { data: capabilitiesData } = useCapabilities();
  const {
    data: subscriptionData,
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
  } = useAdminUserSubscriptions(userId);
  const updateUser = useUpdateUser(String(userId));
  const resetPassword = useResetUserPassword(String(userId));
  const sendTestSms = useSendTestSms(userId);
  const updateSubscriptions = useUpdateAdminUserSubscriptions(userId);
  const setUserActive = useSetUserActive();
  const deleteUser = useDeleteUser();

  const [snack, setSnack] = useState<SnackState>({ open: false, message: '', severity: 'success' });
  const showSnack = (message: string, severity: 'success' | 'error') =>
    setSnack({ open: true, message, severity });

  // Contact
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Password (administrator reset: no current password involved)
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Access
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const [capabilityIds, setCapabilityIds] = useState<number[]>([]);
  const [isStaff, setIsStaff] = useState(false);
  // Confirmations
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!targetUser) return;
    setEmail(targetUser.email ?? '');
    setPhone(targetUser.phone_number ?? '');
    setRoleIds(targetUser.roles.map((r) => r.id));
    setCapabilityIds(targetUser.capabilities.map((c) => c.id));
    setIsStaff(targetUser.is_staff);
  }, [targetUser]);

  if (users && !targetUser) {
    return (
      <>
        <DrawerHeaderBar onClose={onClose} />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">This person is no longer in your organisation.</Alert>
        </Box>
      </>
    );
  }

  if (!targetUser) {
    return (
      <>
        <DrawerHeaderBar onClose={onClose} />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  const isSelf = targetUser.id === currentUser?.id;
  const isLinked = targetUser.auth_source === 'linked';
  const isActive = targetUser.is_active !== false;
  const usesPassword = (targetUser.auth_source ?? 'password') === 'password';
  const linkedOrg = linkedOrganisationName(targetUser);

  const emailDirty = email !== (targetUser.email ?? '');
  const phoneDirty = phone !== (targetUser.phone_number ?? '');
  const savedPhone = (targetUser.phone_number ?? '').trim();
  // The test goes to the *saved* number: a dirty field would silently test a
  // different number than the one on screen, so require saving first.
  const testSmsBlocked = !savedPhone
    ? 'Add and save a mobile number first.'
    : phoneDirty
      ? 'Save the mobile number first. The test is sent to the saved number.'
      : '';
  const accessDirty =
    isStaff !== targetUser.is_staff ||
    [...roleIds].sort().join(',') !== targetUser.roles.map((r) => r.id).sort().join(',') ||
    [...capabilityIds].sort().join(',') !==
      targetUser.capabilities.map((c) => c.id).sort().join(',');
  const passwordReady = newPassword.length > 0 && newPassword === confirmPassword;

  const errorMessage = (error: unknown, fallback: string) => {
    const fieldError = Object.values(requestFieldErrors(error))[0];
    return fieldError ?? requestMessage(error, fallback);
  };

  const refreshSubscriptionsHeader = () =>
    // The subscriptions payload carries the person's email/phone (the SMS
    // gate reads it), so contact edits refresh it too.
    queryClient.invalidateQueries({ queryKey: ['admin-user-subscriptions', userId] });

  const handleSaveEmail = async () => {
    try {
      await updateUser.mutateAsync({ email });
      refreshSubscriptionsHeader();
      showSnack('Email updated.', 'success');
    } catch (error) {
      showSnack(errorMessage(error, 'Could not update the email address.'), 'error');
    }
  };

  const handleSavePhone = async () => {
    try {
      await updateUser.mutateAsync({ phone_number: phone });
      refreshSubscriptionsHeader();
      showSnack(phone ? `Saved ${phone}.` : 'Mobile number cleared.', 'success');
    } catch (error) {
      showSnack(errorMessage(error, 'Could not save the mobile number.'), 'error');
    }
  };

  const handleSendTestSms = async () => {
    try {
      const response = await sendTestSms.mutateAsync();
      showSnack(response.message ?? 'Test SMS sent.', 'success');
    } catch (error) {
      showSnack(errorMessage(error, 'Could not send the test SMS.'), 'error');
    }
  };

  const handleResetPassword = async () => {
    try {
      await resetPassword.mutateAsync({ password: newPassword });
      setNewPassword('');
      setConfirmPassword('');
      showSnack('Password reset.', 'success');
    } catch (error) {
      showSnack(errorMessage(error, 'Could not reset the password.'), 'error');
    }
  };

  const handleSaveAccess = async () => {
    try {
      await updateUser.mutateAsync({
        role_ids: roleIds,
        capability_ids: capabilityIds,
        is_staff: isStaff,
      });
      showSnack('Roles and access updated.', 'success');
    } catch (error) {
      showSnack(errorMessage(error, 'Could not update roles and access.'), 'error');
    }
  };

  const handleSetActive = async (activate: boolean) => {
    try {
      await setUserActive.mutateAsync({ userId: targetUser.id, isActive: activate });
      setConfirmDeactivate(false);
      showSnack(
        activate ? `${targetUser.username} can sign in again.` : `${targetUser.username} deactivated.`,
        'success'
      );
    } catch (error) {
      setConfirmDeactivate(false);
      showSnack(errorMessage(error, 'Could not update this person.'), 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUser.mutateAsync(String(targetUser.id));
      setConfirmDelete(false);
      onClose();
    } catch (error) {
      setConfirmDelete(false);
      showSnack(errorMessage(error, 'Could not delete this person.'), 'error');
    }
  };

  const hasPhone = Boolean(subscriptionData?.user.phone_number ?? targetUser.phone_number);
  const sectionIconSx = { color: 'secondary.main' } as const;

  return (
    <>
      <DrawerHeaderBar onClose={onClose}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
            {targetUser.username}
          </Typography>
          {isSelf && <Chip size="small" label="You" />}
          {targetUser.is_staff && <Chip size="small" color="primary" label="Administrator" />}
          {!isActive && <Chip size="small" label="Deactivated" />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {targetUser.email}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {isLinked
            ? 'Signs in through their own organisation'
            : `Signs in with ${signInLabel(targetUser).toLowerCase()}`}
        </Typography>
      </DrawerHeaderBar>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3, '& > .MuiPaper-root': { mb: 2.5 } }}>
        {isLinked && (
          <Alert severity="info" sx={{ mb: 2.5 }}>
            Managed by {linkedOrg ?? 'their own organisation'}. Their details, sign-in and
            roles are set there; here you can only deactivate or reactivate them.
          </Alert>
        )}

        {!isLinked && (
          <SectionCard
            icon={<ContactMailIcon fontSize="small" sx={sectionIconSx} />}
            title="Contact details"
            description="Where this person's notifications are sent. Mobile number in international format, e.g. +61412345678; leave blank for no SMS."
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveEmail}
                disabled={!emailDirty || updateUser.isPending}
              >
                Save
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                size="small"
                type="tel"
                label="Mobile number"
                placeholder="+61412345678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSavePhone}
                disabled={!phoneDirty || updateUser.isPending}
              >
                Save
              </Button>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Tooltip title={testSmsBlocked} arrow>
                {/* span so the tooltip works while disabled */}
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<SmsOutlinedIcon />}
                    onClick={handleSendTestSms}
                    disabled={Boolean(testSmsBlocked) || sendTestSms.isPending}
                  >
                    {sendTestSms.isPending ? 'Sending…' : 'Send test SMS'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </SectionCard>
        )}

        {!isLinked &&
          (usesPassword ? (
            <SectionCard
              icon={<LockOutlinedIcon fontSize="small" sx={sectionIconSx} />}
              title="Password"
              description="Set a new password for this person. They are signed out of their other sessions and use the new password from their next sign-in."
            >
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (passwordReady && !resetPassword.isPending) handleResetPassword();
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  autoComplete="new-password"
                  label="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  autoComplete="new-password"
                  label="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                  helperText={
                    confirmPassword.length > 0 && newPassword !== confirmPassword
                      ? 'Passwords do not match'
                      : ' '
                  }
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={!passwordReady || resetPassword.isPending}
                  >
                    {resetPassword.isPending ? 'Resetting…' : 'Reset password'}
                  </Button>
                </Box>
              </Box>
            </SectionCard>
          ) : (
            <SectionCard
              icon={<LockOutlinedIcon fontSize="small" sx={sectionIconSx} />}
              title="Sign-in"
              description="Signs in with single sign-on, so there is no password to reset. To stop them signing in, deactivate them below."
            >
              <Box />
            </SectionCard>
          ))}

        {!isLinked && (
          <SectionCard
            icon={<AdminPanelSettingsIcon fontSize="small" sx={sectionIconSx} />}
            title="Roles & access"
            description="The roles and extra permissions this person holds, and whether they can manage people and settings."
          >
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={roleIds}
                onChange={(e) => setRoleIds(e.target.value as number[])}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) =>
                  roles
                    .filter((r) => (selected as number[]).includes(r.id))
                    .map((r) => r.name)
                    .join(', ')
                }
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={roleIds.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Extra permissions</InputLabel>
              <Select
                multiple
                value={capabilityIds}
                onChange={(e) => setCapabilityIds(e.target.value as number[])}
                input={<OutlinedInput label="Extra permissions" />}
                renderValue={(selected) =>
                  (capabilitiesData ?? [])
                    .filter((c) => (selected as number[]).includes(c.id))
                    .map((c) => c.name)
                    .join(', ')
                }
              >
                {(capabilitiesData ?? []).map((capability) => (
                  <MenuItem key={capability.id} value={capability.id}>
                    <Checkbox checked={capabilityIds.includes(capability.id)} />
                    <ListItemText primary={capability.name} secondary={capability.description} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Tooltip title={isSelf ? "You can't change your own access." : ''} arrow>
                <span>
                  <FormControlLabel
                    control={
                      <Switch
                        color="secondary"
                        checked={isStaff}
                        disabled={isSelf}
                        onChange={(_, value) => setIsStaff(value)}
                      />
                    }
                    label="Administrator (can manage people and settings)"
                    sx={{ mr: 0 }}
                  />
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveAccess}
                disabled={!accessDirty || updateUser.isPending}
              >
                Save
              </Button>
            </Box>
          </SectionCard>
        )}

        <SectionCard
          icon={<NotificationsActiveIcon fontSize="small" sx={sectionIconSx} />}
          title="Realtime detection notifications"
          description={`Subscribe ${targetUser.username} to email or SMS alerts when detections arrive near a project. Use "All" to subscribe them to every detection type for a project at once.`}
        >
          <SubscriptionsTable
            data={subscriptionData}
            isLoading={subscriptionsLoading}
            error={subscriptionsError}
            hasPhone={hasPhone}
            subject="other"
            onToggle={(projectId, subscriptions, onSettled) =>
              updateSubscriptions.mutate(
                { project_id: projectId, subscriptions },
                {
                  onError: (err) =>
                    showSnack(errorMessage(err, 'Could not update the subscription.'), 'error'),
                  onSettled,
                }
              )
            }
          />
        </SectionCard>

        {!isSelf && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Account
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {isActive
                    ? 'Deactivating signs them out everywhere and stops them signing in until you reactivate them.'
                    : 'Reactivating lets them sign in again with the same account.'}
                </Typography>
                {isActive ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<BlockIcon />}
                    onClick={() => setConfirmDeactivate(true)}
                    disabled={setUserActive.isPending}
                    sx={{ flexShrink: 0 }}
                  >
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => handleSetActive(true)}
                    disabled={setUserActive.isPending}
                    sx={{ flexShrink: 0 }}
                  >
                    Reactivate
                  </Button>
                )}
              </Box>
              {!isLinked && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Deleting removes their account and settings for good. Deactivate instead if
                    they may come back.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setConfirmDelete(true)}
                    disabled={deleteUser.isPending}
                    sx={{ flexShrink: 0 }}
                  >
                    Delete
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <ConfirmDialog
        open={confirmDeactivate}
        title={`Deactivate ${targetUser.username}?`}
        body="They are signed out everywhere and can't sign in, including through single sign-on, until you reactivate them."
        confirmLabel="Deactivate"
        destructive
        busy={setUserActive.isPending}
        onConfirm={() => handleSetActive(false)}
        onCancel={() => setConfirmDeactivate(false)}
      />
      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${targetUser.username}?`}
        body="Their account and settings are removed for good. This can't be undone."
        confirmLabel="Delete"
        destructive
        busy={deleteUser.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}

/** The sticky top strip of the drawer: identity on the left, close on the right. */
function DrawerHeaderBar({ onClose, children }: { onClose: () => void; children?: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 3,
        py: 2,
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {children}
      </Box>
      <IconButton onClick={onClose} aria-label="Close" edge="end">
        <CloseIcon />
      </IconButton>
    </Box>
  );
}
